import React from "react";
import { UnitAmount } from "../UnitAmount";
import {
  IngredientRestoreRow,
  IngredientRow,
} from "../dish/IngredientRow";
import { FoodUnit } from "../../data/foodUnit";
import { MealIngredient } from "../../data/mealWithIngredients";
import { IntakeWithEditedFlags } from "../../data/plan";
import { isDeleted } from "../../utils/editUtils";
import {
  usePurchase,
  usePurchasesByIds,
  usePurchasesByFood,
} from "../../queries/caches";
import {
  convertByNetWeight,
  formatPurchaseDescription,
} from "../../utils/purchaseUtils";
import { nutriValuesFromIntake } from "../../utils/planUtils";
import {
  IntakeStepInfo,
  mapIntakeStepInfos,
} from "./consumedIntakeUtils";

function ConsumedIntakeRow(props: {
  intake: IntakeWithEditedFlags;
  isLast: boolean;
  stepInfo?: IntakeStepInfo;
  onPatch: (mutate: (i: IntakeWithEditedFlags) => void) => void;
  onOpenProductPicker: () => void;
  onRemove: () => void;
}) {
  const { intake } = props;
  const purchase = usePurchase(intake.purchaseUnitPurchaseId);
  const alternatives = usePurchasesByFood(purchase?.foodId);
  const units: FoodUnit[] = (purchase?.purchaseUnits ?? []).map((pu) => ({
    id: pu.id,
    description: pu.description,
    netWeight: pu.netWeight,
    isDefault: pu.isDefault,
  }));
  const currentUnit =
    units.find((u) => u.id === intake.purchaseUnitId) ?? units[0];
  const name =
    formatPurchaseDescription(purchase) ||
    intake.purchaseUnitPurchaseDescription;
  const showProductPicker = alternatives.length > 1;

  return (
    <IngredientRow
      isLast={props.isLast}
      title={name}
      stepInfo={
        props.stepInfo
          ? { index: props.stepInfo.stepIndex, text: props.stepInfo.stepText }
          : undefined
      }
      amount={
        <UnitAmount
          foodUnit={currentUnit}
          units={units}
          amount={intake.amount}
          onUnitChange={(u) =>
            props.onPatch((i) => {
              i.amount = convertByNetWeight(
                i.amount,
                currentUnit?.netWeight,
                u?.netWeight,
              );
              i.purchaseUnitId = u?.id ?? -1;
              i.purchaseUnitDescription = u?.description ?? "";
            })
          }
          onAmountChange={(a) =>
            props.onPatch((i) => {
              i.amount = a;
            })
          }
        />
      }
      onDelete={props.onRemove}
      deleteLabel="Eintrag entfernen"
      nutri={purchase ? nutriValuesFromIntake(intake, [purchase]) : undefined}
      product={
        showProductPicker
          ? { highlighted: false, onClick: props.onOpenProductPicker }
          : null
      }
    />
  );
}

function ConsumedRestoreRow(props: {
  intake: IntakeWithEditedFlags;
  onRestore: () => void;
}) {
  const purchase = usePurchase(props.intake.purchaseUnitPurchaseId);
  const name =
    formatPurchaseDescription(purchase) ||
    props.intake.purchaseUnitPurchaseDescription ||
    "Eintrag";
  return <IngredientRestoreRow name={name} onRestore={props.onRestore} />;
}

export function ConsumedIntakeEditor(props: {
  intakes: IntakeWithEditedFlags[];
  mealIngredients?: MealIngredient[];
  onPatchIntake: (
    index: number,
    mutate: (i: IntakeWithEditedFlags) => void,
  ) => void;
  onOpenProductPicker: (index: number) => void;
  onRemoveIntake: (index: number) => void;
}) {
  const purchaseIds = props.intakes.map(
    (intake) => intake.purchaseUnitPurchaseId,
  );
  const purchases = usePurchasesByIds(purchaseIds);
  const stepInfos = mapIntakeStepInfos(
    props.mealIngredients ?? [],
    purchases,
    props.intakes,
  );

  const active = props.intakes
    .map((intake, index) => ({ intake, index }))
    .filter(({ intake }) => !isDeleted(intake))
    .sort(
      (a, b) =>
        (a.intake.stepIndex ?? Number.MAX_SAFE_INTEGER) -
        (b.intake.stepIndex ?? Number.MAX_SAFE_INTEGER),
    );

  const deleted = props.intakes
    .map((intake, index) => ({ intake, index }))
    .filter(({ intake }) => isDeleted(intake));

  return (
    <div className="flex w-full flex-col">
      {active.map(({ intake, index }, i) => (
        <ConsumedIntakeRow
          key={index}
          intake={intake}
          isLast={i === active.length - 1}
          stepInfo={stepInfos[index]}
          onPatch={(mutate) => props.onPatchIntake(index, mutate)}
          onOpenProductPicker={() => props.onOpenProductPicker(index)}
          onRemove={() => props.onRemoveIntake(index)}
        />
      ))}
      {deleted.map(({ intake, index }) => (
        <ConsumedRestoreRow
          key={index}
          intake={intake}
          onRestore={() =>
            props.onPatchIntake(index, (i) => {
              i.deleted = false;
            })
          }
        />
      ))}
    </div>
  );
}
